import React, { memo, useMemo, lazy, Suspense, useState } from "react";
import { Loading } from "components/shared";
import { useDispatch, useSelector } from "react-redux";
import {
  LAYOUT_TYPE_CLASSIC,
  LAYOUT_TYPE_MODERN,
  LAYOUT_TYPE_SIMPLE,
  LAYOUT_TYPE_STACKED_SIDE,
  LAYOUT_TYPE_DECKED,
  LAYOUT_TYPE_BLANK,
} from "constants/theme.constant";
import useAuth from "utils/hooks/useAuth";
import useDirection from "utils/hooks/useDirection";
import useLocale from "utils/hooks/useLocale";
import { TourProvider } from "@reactour/tour";
import { TourSteps } from "constants/tourSteps";
import { components } from "@reactour/tour";
import { selectOnboardingTour, setOnboarding } from "store/app/slice";

const TRBadge = ({ children }) => {
  return (
    <components.Badge
      styles={{ badge: (base) => ({ ...base, backgroundColor: "#ec7211" }) }}
    >
      {children}
    </components.Badge>
  );
};

const layouts = {
  [LAYOUT_TYPE_CLASSIC]: lazy(() => import("./ClassicLayout")),
  [LAYOUT_TYPE_MODERN]: lazy(() => import("./ModernLayout")),
  [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import("./StackedSideLayout")),
  [LAYOUT_TYPE_SIMPLE]: lazy(() => import("./SimpleLayout")),
  [LAYOUT_TYPE_DECKED]: lazy(() => import("./DeckedLayout")),
  [LAYOUT_TYPE_BLANK]: lazy(() => import("./BlankLayout")),
};

const Layout = () => {
  const layoutType = useSelector((state) => state.theme.layout.type);
  const onboardingPages = useSelector(selectOnboardingTour);
  const { authenticated } = useAuth();
  const dispatch = useDispatch();

  useDirection();

  useLocale();

  const AppLayout = useMemo(() => {
    if (authenticated) {
      return layouts[layoutType];
    }
    // console.log({ layoutType });
    return lazy(() => import("./AuthLayout"));
  }, [layoutType, authenticated]);

  const handleClose = ({ setCurrentStep, currentStep, steps, setIsOpen }) => {
    setIsOpen(false);

    Object.keys(onboardingPages).forEach((page) => {
      dispatch(setOnboarding({ page }));
    });
  };

  return (
    <Suspense
      fallback={
        <div className="flex flex-auto items-center justify-center flex-col min-h-[100vh]">
          <Loading loading={true} />
        </div>
      }
    >
      <TourProvider
        showDots={false}
        disableInteraction
        steps={TourSteps}
        components={{ Badge: TRBadge }}
        className="rounded-xl"
        disableDotsNavigation
        onClickClose={handleClose}
        onClickMask={handleClose}
      >
        <AppLayout />
      </TourProvider>
    </Suspense>
  );
};

export default memo(Layout);
